import type { CompanySummary } from './types';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import type { ReportSummary } from '@/data/repos/reportsRepo';
import { describeMonthWindow, type MonthWindowDescriptor } from '../../../../utils/periodicity';
import { formatIsoDate, formatIsoDateItShort } from '@/utils/date';
import { resolveAddress } from '@/utils/address';
import { deriveScheduleState, resolvePlannedVisitAt } from '@/utils/inspectionScheduling';

const DAY_MS = 24 * 60 * 60 * 1000;
const SOON_THRESHOLD_DAYS = 30;

export const soonThreshold = (now: number): number => now + SOON_THRESHOLD_DAYS * DAY_MS;

const parseDate = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const time = Date.parse(value);
  return Number.isNaN(time) ? undefined : time;
};

const resolveNextVisitWindow = (time?: number): MonthWindowDescriptor | undefined => {
  if (typeof time !== 'number') return undefined;
  return describeMonthWindow(time);
};

const buildReportsMap = (reports: ReportSummary[]): Map<string, ReportSummary[]> =>
  reports.reduce((map, report) => {
    const bucket = map.get(report.companyId);
    if (bucket) {
      bucket.push(report);
    } else {
      map.set(report.companyId, [report]);
    }
    return map;
  }, new Map<string, ReportSummary[]>());

export const createSummaries = (
  companies: CompanyWithSites[] | undefined,
  reports: ReportSummary[],
  now: number,
  threshold: number
): CompanySummary[] => {
  if (!companies) return [];

  const reportsByCompany = buildReportsMap(reports);

  return companies.map((company) => {
    const companyReports = reportsByCompany.get(company.id) ?? [];
    const finalizedReports = companyReports.filter((report) => report.status === 'finalized');
    const reportTimes = companyReports
      .map((report) => parseDate(report.visitDate))
      .filter((time): time is number => typeof time === 'number');
    const lastReportAt = reportTimes.length ? Math.max(...reportTimes) : undefined;

    const siteForecasts = company.sites.map((site) => {
      const siteReports = finalizedReports.filter((report) => report.siteId === site.id);
      const siteReportTimes = siteReports
        .map((report) => parseDate(report.visitDate))
        .filter((time): time is number => typeof time === 'number');
      const siteLastReport = siteReportTimes.length ? Math.max(...siteReportTimes) : undefined;
      const plannedVisitAt = resolvePlannedVisitAt(site);
      const lastFinalizedIso =
        siteLastReport !== undefined ? formatIsoDate(new Date(siteLastReport)) : undefined;
      const schedule = deriveScheduleState({
        contractActivationDate: site.contractActivationDate,
        periodicityMonths: site.periodicity ? parseInt(site.periodicity, 10) : undefined,
        lastFinalizedReportAt: lastFinalizedIso,
        nextPlannedVisitAt: plannedVisitAt,
      });
      const expectedMonth = schedule.expectedMonth;
      if (plannedVisitAt) {
        const plannedTime = Date.parse(plannedVisitAt);
        return Number.isNaN(plannedTime)
          ? null
          : {
              time: plannedTime,
              label: formatIsoDateItShort(plannedVisitAt),
            };
      }
      if (expectedMonth) {
        return { time: expectedMonth.getTime() };
      }
      return null;
    });

    const predictedNextVisit = siteForecasts
      .filter((entry): entry is { time: number; label?: string } => entry !== null)
      .sort((a, b) => a.time - b.time)[0];

    const nextVisitAt = predictedNextVisit?.time;
    const dueSoon =
      typeof nextVisitAt === 'number' && nextVisitAt >= now && nextVisitAt <= threshold;
    const overdue = typeof nextVisitAt === 'number' && nextVisitAt < now;
    const ncOpen = companyReports.reduce((total, report) => total + report.nonConformitiesOpen, 0);

    const nextVisitWindow = resolveNextVisitWindow(nextVisitAt);
    const nextVisitLabel = (() => {
      if (predictedNextVisit?.label) return predictedNextVisit.label;
      if (typeof nextVisitAt !== 'number') {
        return finalizedReports.length > 0 ? 'Da pianificare' : 'Nessun audit registrato';
      }
      return nextVisitWindow?.label ?? 'Da pianificare';
    })();

    const unitsCount = company.sites.reduce((total, site) => {
      const hasUnitsArray = Array.isArray(site.refrigerationUnits);
      const explicitUnits = hasUnitsArray ? site.refrigerationUnits.length : null;
      const fallbackUnits =
        typeof site.refrigerationUnitsCount === 'number' ? site.refrigerationUnitsCount : null;

      if (explicitUnits !== null && explicitUnits > 0) {
        return total + explicitUnits;
      }

      if (fallbackUnits !== null && fallbackUnits > 0) {
        return total + fallbackUnits;
      }

      return total;
    }, 0);

    return {
      id: company.id,
      name: company.name,
      code: company.code,
      logoUrl: company.logoUrl,
      address: resolveAddress(
        {
          address: company.sedeLegaleAddress,
          cap: company.sedeLegaleCap,
          city: company.sedeLegaleCity,
          province: company.sedeLegaleProvince,
        },
        company.sedeLegale
      ),
      sitesCount: company.sites.length,
      siteNames: company.sites.map((site) => site.name),
      lastReportAt,
      nextVisitAt,
      nextVisitWindow,
      nextVisitLabel,
      ncOpen,
      totalReports: companyReports.length,
      dueSoon,
      overdue,
      hasReports: companyReports.length > 0,
      unitsCount,
    };
  });
};
